import { useState } from "react";
import {
  BarChart3, Sparkles, TrendingUp, AlertTriangle, ShieldCheck,
  Download, ChevronRight, ChevronLeft, X, CheckCircle2, Eye, UploadCloud
} from "lucide-react";
import { toast } from "../../../lib/toast";
import { useSetup } from "../SetupContext";

const SIGNOFFS = [
  { name: "By Marcus L.", type: "Cleaning sign-off", status: "approved" as const },
  { name: "By Ananya R.", type: "Consolidation sign-off", status: "pending"  as const },
  { name: "By Sofia W.", type: "Classification sign-off", status: "review"   as const },
];

const ModuleUpdates = [
  { name: "Executive Dashboard", type: "KPIs, AI insights, charts, priority panel" as const },
  { name: "Opportunity Insights", type: "Savings recommendations, advisor outputs"  as const }
];

export function PublishDashboardStep({ onNext, onBack, onCancel }: { onNext: () => void; onBack: () => void; onCancel: () => void }) {
  const { form, setField } = useSetup();
  const [activeModule, setActiveModule] = useState<string>("");

  return (
    <div className="setup-screen pad-0 bg-transparent">
        <div className="border border-color-d4 bg-white radius-10">
            <div className="border-bottom pad-12">
                <div className="sidebar-title">Human review queue</div>
                <div className="sidebar-sub">Before human review</div>
            </div>
            <div className="pad-12">
                <div className="form-grid margin-0">
                    <div className="form-group">
                    <label className="form-group__label" htmlFor="refreshFreq">Client</label>
                    <div className="form-group__select-wrap">
                    <select id="refreshFreq" className="form-group__select">
                        <option>Coca-Cola Company</option>
                        <option>Magna International</option>
                        <option>Nsk  India  Ltd  Pvt.</option>
                        <option>India Hutchinson Automotive</option>
                    </select>
                    </div>
                </div>
                <div className="form-group">
                    <label className="form-group__label" htmlFor="refreshFreq">Project</label>
                    <div className="form-group__select-wrap">
                    <select id="refreshFreq" className="form-group__select">
                        <option>Project 1</option>
                        <option>Project 2</option>
                        <option>Project 3</option>
                        <option>Project 4</option>
                    </select>
                    </div>
                </div>
                </div>
            </div>
        </div>

        <div className="border border-color-d4 bg-white radius-10">
            <div className="border-bottom pad-12">
                <div className="sidebar-title">Upload Final Dataset</div>
                <div className="sidebar-sub">Latest classified dataset from the workflow.</div>
            </div>
            <div className="pad-12">
                <div className="file-upload-zone"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => { e.preventDefault();
                        const file = e.dataTransfer.files[0];
                        if (file) { setField("uploadedFile", file.name); toast.success(`Uploaded: ${file.name}`); }
                    }}
                    style={{ textAlign: "center" }}>
                    <UploadCloud size={24} style={{ margin: "0 auto 8px", color: "var(--text-muted)" }} aria-hidden="true" />
                    <div className="file-upload-zone__label">
                        {form.uploadedFile ? `✓ ${form.uploadedFile}` : "Browse or drop"}
                    </div>
                    <div className="file-upload-zone__hint">CSV or Excel. Drag & drop or click to browse.</div>
                    <input type="file" accept=".csv,.xlsx,.xls" style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", height: "100%" }}
                        onChange={(e) => { const file = e.target.files?.[0]; if (file) { setField("uploadedFile", file.name); toast.success(`Uploaded: ${file.name}`); }
                    }}
                    aria-label="Upload taxonomy file"
                    />
                </div>
                <div className="signoff-card-wrapper gap-2">
                    {SIGNOFFS.map((s) => (
                    <div key={s.name} className="signoff-card">
                        <div className="signoff-card__user">
                        <span className={`signoff-status`}>
                            {s.status === "approved" && 
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="#00A63E"/>
                                <path d="M14 7L8.5 12.5L6 10" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            }
                            {s.status === "pending"  && 
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="#00A63E"/>
                                <path d="M14 7L8.5 12.5L6 10" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            }
                            {s.status === "review"   && 
                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M0 10C0 4.47715 4.47715 0 10 0C15.5228 0 20 4.47715 20 10C20 15.5228 15.5228 20 10 20C4.47715 20 0 15.5228 0 10Z" fill="#00A63E"/>
                                <path d="M14 7L8.5 12.5L6 10" stroke="white" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            }
                        </span>
                        <div className="initials">
                            <div style={{ fontSize: 14, color: "var(--text-primary)" }}>{s.type}</div>
                            <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{s.name}</div>
                        </div>
                        </div>
                    </div>
                    ))}
                </div>
            </div>
        </div>
      
      <div className="border border-color-d4 bg-white radius-10">
        <div className="border-bottom pad-12">
            <div className="sidebar-title">What will Update</div>
            <div className="sidebar-sub">Modules that will reflect the new dataset.</div>
        </div>
        <div className="pad-12">
            <div className="module-card-wrapper gap-2 grid">
                {ModuleUpdates.map((m) => (
                <div
                  key={m.name}
                  className="module-card border border-color-d4 radius-10 pad-12 cursor-pointer"
                  onClick={() => setActiveModule(m.name)}
                  style={
                    activeModule === m.name
                      ? {
                          backgroundColor: "#FCFBFD",
                          borderColor: "#6b3fa0",
                          boxShadow: "0 0 0 3px rgba(107, 63, 160, 0.10)",
                        }
                      : undefined
                  }
                >
                    <div className="module-card__name"><strong>{m.name}</strong></div>
                    <div className="module-card__type">{m.type}</div>
                </div>
                ))}
            </div>
        </div>
      </div>

      <div className="wizard-action-bar">
        <button type="button" className="action-bar__cancel btn btn--outline" onClick={onCancel}>
          Cancel
        </button>
        <button type="button" className="btn btn--outline m-left-auto" onClick={onBack}>
          <ChevronLeft size={14} /> Back
        </button>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => {
            onNext();
          }}>
        <svg width="15" height="13" viewBox="0 0 15 13" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0.666016 6.48828H0.912866" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M0.789062 6.36719L0.912488 6.49061L0.789062 6.61404" stroke="white" stroke-width="1.33333" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M6.06055 9.9375V12.5544C6.06055 12.7372 6.17791 12.8991 6.35169 12.9567C6.39513 12.9708 6.43971 12.9776 6.48372 12.9776C6.61575 12.9776 6.74326 12.9155 6.82451 12.8049L8.35527 10.7218L6.06055 9.9375Z" fill="white"/>
            <path d="M14.4888 0.0784779C14.359 -0.0134915 14.1886 -0.0259045 14.0476 0.0480095L1.35241 6.6777C1.20232 6.75613 1.11374 6.91637 1.12615 7.08508C1.13913 7.25435 1.25141 7.39879 1.41109 7.45352L4.94034 8.65984L12.4564 2.23327L6.64037 9.24043L12.5552 11.2621C12.5992 11.2767 12.6455 11.2846 12.6917 11.2846C12.7685 11.2846 12.8446 11.2638 12.9118 11.2231C13.019 11.1577 13.0918 11.0477 13.1104 10.9241L14.662 0.485851C14.6851 0.327867 14.6186 0.171011 14.4888 0.0784779Z" fill="white"/>
        </svg>
          Publish  to Dashboard
        </button>
      </div>
    </div>
  );
}
